
import { Vue, Options } from "vue-class-component";
import "@Moodbuster2.0/mb2-components-vue/dist/mb2-components-vue.css";
import { Agenda } from "@Moodbuster2.0/mb2-components-vue";
import EventModal from "@/components/EventModal.vue";
import ApiService from "../services/ApiService";
import StorageService from "@/services/StorageService";
import { wrapper } from "../utils/wrapper";
import "vue-cal/dist/i18n/pt-br.js";
import "vue-cal/dist/i18n/nl.js";
import "vue-cal/dist/i18n/uk.js";
import "vue-cal/dist/vuecal.css";
import "vue-cal/dist/drag-and-drop.js";
import { useStore } from "vuex";
import { uuid } from "vue-uuid";
import _ from "lodash";
import { formatEventForApi, addMissingKeys, formatEventForUpdateApi, formatTimeSeriesEventForUpdateApi, formatEventForVueCal } from "@/utils/events";

@Options({
  components: {
    Agenda,
    EventModal
  }
})
export default class MyCalendar extends Vue {
  private apiService = new ApiService();
  private storageService = new StorageService();
  globalStore = useStore();
  showModal = false;
  mode = "add";
  user: any = null;
  currentEvent: any = {};
  originalStartTime = '';
  originalEndTime = '';

  // local events array
  events: any[] = [];

  // sets VueCal language
  currentLocale() {
    const current = this.$i18n?.locale;
    if (current === "pt") return "pt-br";
    if (current === "nl") return "nl";
    if (current === "uk") return "uk";
    return "en";
  }

  // Runs when the page is created
  async created() {
    await this.getUser();
    await this.getEvents();
  }

  async getUser() {
    this.user = this.storageService.getUser();
  }

  moveHintBox(x: any, y: any) {
    const d: any = document.querySelector(".box");
    d.style.left = x + "px";
    d.style.top = y - d.offsetHeight + "px";
  }
  resetHintBox() {
    const d: any = document.querySelector(".box");
    d.style.left = "50%";
    d.style.top = "50%";
  }

  async showHintMessage() {
    this.globalStore.commit("open", {
      type: "info",
      text: this.$t("SCHEDULE.HINT_MESSAGE")
    });
    onmousemove = e => {
      this.moveHintBox(e.clientX, e.clientY);
    };
    onmousedown = () => {
      this.hideHintMessage();
    };
  }

  async hideHintMessage() {
    this.globalStore.commit("close", {
      type: "info",
      text: this.$t("SCHEDULE.HINT_MESSAGE")
    });
    onmousemove = null;
    onmousedown = null;
    this.resetHintBox();
  }

  /*
    Gets the events from the API.
    Data transformation is done to comply with the format required by vue-cal.
  */
  async getEvents() {
    const processedEvents = [];

    const dataResponse = await wrapper(
      this.apiService.getEvents(this.user.patientsId)
    );
    if (dataResponse.error) {
      this.globalStore.commit("open", {
        type: "danger",
        title: this.$t("ALERTS.EVENTS.GET.FAILED.TITLE"),
        text: this.$t("ALERTS.EVENTS.GET.FAILED.TEXT")
      });
    }

    if (dataResponse.data) {
      for (const e of dataResponse.data) {
        processedEvents.push(formatEventForVueCal(e));
      }
    }

    this.events = processedEvents;
  }

  /*
    Opens the event modal.
    Note when the update is called the type parameter is an object and does not contain the string 'update'
  */
  async openEventModal(event: any, type: string) {
    // Adding a uuid to the event that is stored in the events array
    // eases the process of finding the event in the events array
    if (!event.uuid) {
      event.uuid = uuid.v4();
    }

    this.resetCurrent();

    this.originalStartTime = event.startsAt;
    this.originalEndTime = event.endsAt;

    this.currentEvent.uuid = event.uuid;
    this.currentEvent.end = event.end;
    this.currentEvent.start = event.start;
    this.currentEvent.allDay = event.allDay;
    this.currentEvent.pagesElementsId = event.pagesElementsId;
    this.currentEvent.templatesElementsId = event.templatesElementsId;

    if (typeof type == "string" && type == "add") {
      this.mode = "add";
      this.events.push(event);
    } else {
      this.mode = "update";
      this.currentEvent.id = event.id;
      this.currentEvent.title = event.title;
      this.currentEvent.color = event.class;
      this.currentEvent.content = event.content;
      this.currentEvent.completed = event.completed;
      // when the event is completed then the event cannot be edited
      this.currentEvent.editable = !event.completed;
      this.currentEvent.resizable = !event.completed;
      // when the event has an associated answer then the event cannot be deleted
      this.currentEvent.hasAnswer = (await this.checkAnswerExists(event)) ? true : false;
    }

    this.showModal = true;
    return event;
  }

  async checkAnswerExists(event: any) {
    const result = await wrapper(this.apiService.checkEventAnswer(event.id, this.user.patientsId))
    if (result.data.error) return true
    else return result.data.data
  }

  // method called after closing 'add new event' modal
  async createEvent(data: any) {
    this.showModal = false;

    const events = formatEventForApi(data, this.user.language);
    for (const event of events) {
      const eventSent = await wrapper(
        this.apiService.createEvent(event, this.user.patientsId)
      );

      if (eventSent.error) {
        console.error("data error ", eventSent.error);
        this.globalStore.commit("open", {
          type: "danger",
          title: this.$t("ALERTS.EVENTS.CREATE.FAILED.TITLE"),
          text: this.$t("ALERTS.EVENTS.CREATE.FAILED.TEXT")
        });
        this.removeEvent(data.uuid);
      }
      if (eventSent.data) {
        data.id = eventSent.data.id;
        this.updateEventsArray(data);
      }
    }
    await this.getEvents()
  }

  // click and hold calendar event to delete
  async deleteEvent(event: any) {
    this.removeEvent(event.uuid);

    const response = await wrapper(
      this.apiService.deleteEvent(event.id, this.user.patientsId)
    );

    if (response.error) {
      console.error("delete events error ", response.error);
      this.globalStore.commit("open", {
        type: "danger",
        title: this.$t("ALERTS.EVENTS.DELETE.FAILED.TITLE"),
        text: this.$t("ALERTS.EVENTS.DELETE.FAILED.TEXT")
      });
      // restores the event on the calendar events array
      this.events.push(event);
    }
    // event got deleted
  }

  // method called after closing 'update event' modal
  async updateEvent(data: any) {
    if (this.showModal) {
      this.showModal = false;
    }
    if (data.data) data = data.data;

    const event = formatEventForUpdateApi(data);

    const timeSeries = await wrapper(this.apiService.getTimeSeries(this.user.patientsId, event.id))
    if (timeSeries.data.some((obj: { id: any; }) => obj.id == event.id)) {
      event.rrule = { "timeSeriesId": `${timeSeries.data[0].rrule.timeSeriesId}` }
    }

    if (data.applyRange.range == null || data.applyRange.range == 'current') {
      if (data.toDelete.toDelete) {
        this.deleteEvent(event)
        this.removeEvent(data.uuid)
      } else {
        const eventSent = await wrapper(
          this.apiService.updateEvent(event.id, event, this.user.patientsId)
        );

        if (eventSent.error) {
          this.globalStore.commit("open", {
            type: "danger",
            title: this.$t("ALERTS.EVENTS.UPDATE.FAILED.TITLE"),
            text: this.$t("ALERTS.EVENTS.UPDATE.FAILED.TEXT")
          });
        }
        // event got updated
        if (eventSent.data) {
          this.updateEventsArray(data);
        }
      }
    } else {
      // Delete all events in the series
      if (data.toDelete.toDelete) {
        await this.deleteTimeSeries(timeSeries.data)
      }
      else {
        // Edit all events in the series
        const timeSeriesUuid = uuid.v4()
        for (const singleEvent of timeSeries.data) {
          const updatedEvent = formatTimeSeriesEventForUpdateApi(singleEvent, data, timeSeriesUuid)

          const result = await wrapper(this.apiService.updateEvent(updatedEvent.id, updatedEvent, this.user.patientsId))
          if (result.error) {
            this.globalStore.commit("open", {
              type: "danger",
              title: this.$t("ALERTS.EVENTS.UPDATE_TIMESERIES.FAILED.TITLE"),
              text: this.$t("ALERTS.EVENTS.UPDATE_TIMESERIES.FAILED.TEXT")
            });
          }
        }
        this.events = []
        await this.getEvents()
      }
    }
  }

  async deleteTimeSeries(timeSeries: any) {
    for (const singleEvent of timeSeries) {
      const result = await wrapper(this.apiService.deleteEvent(singleEvent.id, this.user.patientsId))
      if (result.error) {
        this.globalStore.commit("open", {
          type: "danger",
          title: this.$t("ALERTS.EVENTS.DELETE_ALL.FAILED.TITLE"),
          text: this.$t("ALERTS.EVENTS.DELETE_ALL.FAILED.TEXT")
        });
      }
      this.removeEvent(this.events.find(event => event.id == singleEvent.id).uuid)
    }
  }

  // method called when the event is resized
  async durationChangeEvent(event: any) {
    event = addMissingKeys(event.event)
    await this.updateEvent(event);
  }

  // method called after closing without save 'add new event' modal
  cancelEvent(data: any) {
    this.showModal = false;
    this.removeEvent(data.uuid);
  }

  // method called after closing without save 'update event' modal
  cancelUpdateEvent() {
    this.showModal = false;
  }

  // drag and drop calendar event to move
  async moveEvent(event: any) {
    event = addMissingKeys(event.event)
    await this.updateEvent(event);
  }

  // sync local events array after server changes are completed
  updateEventsArray(data: any) {
    const index = this.getEvent(data.uuid);
    this.events[index].title = data.title;
    this.events[index].content = data.content;
    this.events[index].class = data.class || data.color;
    this.events[index].id = data.id;
    this.events[index].start = data.start;
    this.events[index].end = data.end;
    this.events[index].allDay = data.allDay;
    this.events[index].completed = data.completed;
    this.events[index].resizable = !data.completed;
    this.events[index].pagesElementsId = data.pagesElementsId;
    this.events[index].templatesElementsId = data.templatesElementsId;
  }

  // Return event form the events array
  getEvent(uuid: string) {
    return _.findIndex(this.events, e => e.uuid === uuid);
  }

  // Removes event from the events array
  removeEvent(uuid: string) {
    this.events = _.filter(this.events, e => e.uuid !== uuid);
  }

  // Reset the currentEvent
  resetCurrent() {
    this.currentEvent = {
      id: null,
      uuid: "",
      title: "",
      description: "",
      start: "",
      end: "",
      allDay: false,
      completed: false,
      color: "",
      content: "",
      editable: true,
      resizable: true
    };
    this.originalStartTime = '';
    this.originalEndTime = '';
  }
}
